import React from 'react';
import { Box, ButtonBase, Skeleton, Tooltip, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import type { SxProps, Theme } from '@mui/material';
import { ArrowDownRight, ArrowRight, ArrowUpRight, Info } from 'lucide-react';
import { focusRing, sbg } from '../../theme/sbgTokens';
import { useDelayedFlag } from './useDelayedFlag';

export type DeltaTone = 'good' | 'bad' | 'flat';

/**
 * Delta pill: colored by meaning for the reader, not by direction
 * (more absences is `bad` even though the number went up).
 */
export const Delta: React.FC<{ value: React.ReactNode; tone: DeltaTone; direction?: 'up' | 'down' | 'flat'; onBrand?: boolean }> = ({
  value,
  tone,
  direction,
  onBrand = false,
}) => {
  const colors =
    tone === 'good' ? sbg.status.success : tone === 'bad' ? sbg.status.danger : sbg.status.neutral;
  const dir = direction ?? (tone === 'flat' ? 'flat' : undefined);
  const Arrow = dir === 'up' ? ArrowUpRight : dir === 'down' ? ArrowDownRight : dir === 'flat' ? ArrowRight : null;
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.25,
        height: 20,
        px: 0.75,
        borderRadius: sbg.radius.pill,
        font: 'var(--sbg-type-label-xs)',
        fontVariantNumeric: 'tabular-nums',
        whiteSpace: 'nowrap',
        ...(onBrand
          ? { bgcolor: 'rgba(0, 43, 96, 0.24)', color: sbg.text.onBrand }
          : { bgcolor: colors.bg, color: colors.text }),
      }}
    >
      {Arrow && <Arrow size={12} aria-hidden="true" />}
      {value}
    </Box>
  );
};

/** KPI sparkline (book §13.5): 2px Blue line, 8% area, end dot; shape, not values. */
export const Sparkline: React.FC<{ data: number[]; height?: number; onBrand?: boolean; 'aria-label'?: string }> = ({
  data,
  height = 36,
  onBrand = false,
  ...rest
}) => {
  const id = React.useId();
  if (data.length < 2) return null;
  const width = 200;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const span = max - min || 1;
  const pad = 4;
  const points = data.map((value, index) => {
    const x = (index / (data.length - 1)) * width;
    const y = pad + (1 - (value - min) / span) * (height - pad * 2);
    return [x, y] as const;
  });
  const line = points.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  const area = `${line} L${width},${height} L0,${height} Z`;
  const [lastX, lastY] = points[points.length - 1];
  const stroke = onBrand ? sbg.text.onBrand : sbg.action.primary;
  return (
    <Box
      component="svg"
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      role={rest['aria-label'] ? 'img' : undefined}
      aria-label={rest['aria-label']}
      aria-hidden={rest['aria-label'] ? undefined : true}
      sx={{ width: '100%', height, display: 'block', overflow: 'visible' }}
    >
      <defs>
        <clipPath id={`${id}-clip`}>
          <rect x="0" y="0" width={width} height={height} />
        </clipPath>
      </defs>
      <path d={area} fill={stroke} fillOpacity={onBrand ? 0.16 : 0.08} clipPath={`url(#${id}-clip)`} />
      <path d={line} fill="none" stroke={stroke} strokeWidth={2} vectorEffect="non-scaling-stroke" strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={lastX} cy={lastY} r={3} fill={stroke} vectorEffect="non-scaling-stroke" />
    </Box>
  );
};

export interface KpiTileProps {
  /** Overline label: what is counted ("Out today"). */
  label: React.ReactNode;
  /** The value, already formatted with its unit. */
  value: React.ReactNode;
  /** Period or comparison caption ("Oct 1–7" / "vs last week"). Every KPI states its period. */
  caption?: React.ReactNode;
  delta?: { value: React.ReactNode; tone: DeltaTone; direction?: 'up' | 'down' | 'flat' };
  /** Explains the metric in a tooltip on an ⓘ icon. */
  info?: string;
  sparkline?: number[];
  /** Leading icon (rendered in a quiet icon tile). */
  icon?: React.ReactNode;
  /** The single Featured (Lift) tile on a screen. At most one per screen. */
  featured?: boolean;
  /** `md` uses numeric-md (24/32) for dense rows; default numeric-lg (32/40). */
  size?: 'lg' | 'md';
  /** Shows skeletons for the value and caption (after a 300ms delay, so fast loads never flash). */
  loading?: boolean;
  onClick?: () => void;
  /** In-app route: renders a router link (middle-click and new-tab work). */
  to?: string;
  /** External or full-reload URL. Prefer `to` for app routes. */
  href?: string;
  /** For tiles that act as filter toggles: sets aria-pressed and the selected look. */
  selected?: boolean;
  'aria-label'?: string;
  sx?: SxProps<Theme>;
}

/**
 * KPI tile (book §11.9): overline label, Host Grotesk value, delta + caption,
 * optional sparkline. A row holds at most four.
 */
const KpiTile: React.FC<KpiTileProps> = ({
  label,
  value,
  caption,
  delta,
  info,
  sparkline,
  icon,
  featured = false,
  size = 'lg',
  loading = false,
  onClick,
  to,
  href,
  selected,
  sx,
  ...rest
}) => {
  const interactive = Boolean(onClick || href || to);
  const onBrand = featured;
  const showSkeleton = useDelayedFlag(loading, 300);
  const skeletonSx = { visibility: showSkeleton ? 'visible' : 'hidden' } as const;
  const content = (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
        {icon && (
          <Box
            aria-hidden="true"
            sx={{
              width: 32,
              height: 32,
              borderRadius: sbg.radius.md,
              display: 'grid',
              placeItems: 'center',
              flex: 'none',
              bgcolor: onBrand ? 'rgba(255, 255, 255, 0.16)' : sbg.bg.tile,
              color: onBrand ? sbg.text.onBrand : sbg.icon.tile,
              '& > svg': { width: 16, height: 16 },
            }}
          >
            {icon}
          </Box>
        )}
        <Typography
          variant="overline"
          component="span"
          sx={{ color: onBrand ? sbg.text.onBrand : sbg.text.secondary, minWidth: 0, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          {label}
        </Typography>
        {info && (
          <Tooltip title={info}>
            <Box
              component="span"
              tabIndex={0}
              role="img"
              aria-label={info}
              sx={{ display: 'inline-flex', color: onBrand ? sbg.text.onBrand : sbg.text.secondary, borderRadius: '50%', '&:focus-visible': focusRing }}
            >
              <Info size={14} />
            </Box>
          </Tooltip>
        )}
      </Box>
      {loading ? (
        <Skeleton variant="rounded" width="55%" height={size === 'md' ? 32 : 40} sx={{ my: 0.25, ...skeletonSx }} />
      ) : (
        <Typography
          variant={size === 'md' ? 'numericMd' : 'numericLg'}
          component="p"
          sx={{ color: onBrand ? sbg.text.onBrand : sbg.text.primary, overflowWrap: 'anywhere' }}
        >
          {value}
        </Typography>
      )}
      {(delta || caption) && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', minHeight: 20 }}>
          {delta && !loading && <Delta {...delta} onBrand={onBrand} />}
          {caption && loading && <Skeleton variant="rounded" width="70%" height={14} sx={skeletonSx} />}
          {caption && !loading && (
            <Typography variant="body2" component="span" sx={{ color: onBrand ? sbg.text.onBrand : sbg.text.secondary }}>
              {caption}
            </Typography>
          )}
        </Box>
      )}
      {sparkline && sparkline.length > 1 && !loading && (
        <Box sx={{ mt: 0.5 }}>
          <Sparkline data={sparkline} onBrand={onBrand} />
        </Box>
      )}
    </>
  );

  const surface = {
    display: 'grid',
    alignContent: 'start',
    // ButtonBase centers its children by default; tiles read left to right.
    justifyContent: 'stretch',
    justifyItems: 'stretch',
    gap: 0.75,
    width: '100%',
    height: '100%',
    minWidth: 0,
    textAlign: 'left' as const,
    p: featured ? 3 : 2.5,
    borderRadius: featured ? sbg.radius.panel : sbg.radius.card,
    ...(featured
      ? { background: sbg.gradient.lift, color: sbg.text.onBrand, border: 0 }
      : selected
        ? { bgcolor: sbg.bg.selected, border: `1px solid ${sbg.border.selected}`, boxShadow: `inset 0 0 0 1px ${sbg.border.selected}` }
        : { bgcolor: sbg.bg.surface, border: `1px solid ${sbg.border.default}` }),
    transition: `border-color ${sbg.duration.fast} ${sbg.ease.standard}, box-shadow ${sbg.duration.fast} ${sbg.ease.standard}`,
    ...(interactive && !featured
      ? { cursor: 'pointer', '&:hover': { borderColor: selected ? sbg.border.selected : sbg.border.strong, boxShadow: selected ? `inset 0 0 0 1px ${sbg.border.selected}` : sbg.shadow.sm } }
      : null),
    ...(interactive && featured ? { cursor: 'pointer', '&:hover': { filter: 'brightness(1.04)' } } : null),
    '&.Mui-focusVisible': focusRing,
    ...sx,
  };

  if (interactive) {
    return (
      <ButtonBase
        {...(to ? { component: RouterLink, to } : href ? { component: 'a', href } : null)}
        onClick={onClick}
        aria-label={rest['aria-label']}
        aria-pressed={selected === undefined ? undefined : selected}
        aria-busy={loading || undefined}
        sx={surface}
      >
        {content}
      </ButtonBase>
    );
  }
  return (
    <Box aria-label={rest['aria-label']} aria-busy={loading || undefined} sx={surface}>
      {content}
    </Box>
  );
};

export default KpiTile;

/**
 * A row of KPI tiles: up to four columns, collapsing to two then one.
 * `phoneColumns={2}` keeps short count tiles two-up on phones.
 */
export const KpiGrid: React.FC<{ children: React.ReactNode; columns?: 2 | 3 | 4; phoneColumns?: 1 | 2; sx?: SxProps<Theme> }> = ({
  children,
  columns = 4,
  phoneColumns = 1,
  sx,
}) => (
  <Box
    sx={{
      display: 'grid',
      gap: 2,
      gridTemplateColumns: {
        xs: phoneColumns === 2 ? 'repeat(2, minmax(0, 1fr))' : '1fr',
        sm: 'repeat(2, minmax(0, 1fr))',
        lg: `repeat(${columns}, minmax(0, 1fr))`,
      },
      mb: 3,
      ...sx,
    }}
  >
    {children}
  </Box>
);
